import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { renderTextWithLinks, formatRelative } from '../utils/notesHelpers';

/* ═══════════════════════════════════════════════════
   NotesView — notepad peribadi
   Storage: Firestore users/{uid}/notes (sync semua device)
   Fields: { id, title, text, pinned, isQuickNote, createdAt, updatedAt }
   - pinned     → nota naik ke atas page Notes
   - isQuickNote → nota muncul dalam panel Quick Notes (Workspace)
   ═══════════════════════════════════════════════════ */

const NotesView = ({ onMenuClick }) => {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [draftTitle, setDraftTitle] = useState('');
    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editText, setEditText] = useState('');
    const [copiedKey, setCopiedKey] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // ─── Load notes (real-time, auto-sync semua device) ───
    useEffect(() => {
        if (!auth.currentUser) { setIsLoading(false); return; }
        const notesRef = collection(db, 'users', auth.currentUser.uid, 'notes');
        const unsub = onSnapshot(
            notesRef,
            (snap) => {
                const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                items.sort((a, b) => {
                    const ap = a.pinned ? 1 : 0;
                    const bp = b.pinned ? 1 : 0;
                    if (ap !== bp) return bp - ap;                       // pinned dulu
                    return (b.createdAt || 0) - (a.createdAt || 0);      // newest first
                });
                setNotes(items);
                setIsLoading(false);
            },
            (err) => {
                console.error('[Notes] load error:', err);
                setIsLoading(false);
            }
        );
        return () => unsub();
    }, []);

    // ─── Tambah nota baru ───
    const handleAdd = async () => {
        const title = draftTitle.trim();
        const text = draft.trim();
        if ((!title && !text) || !auth.currentUser || isSaving) return;
        setIsSaving(true);
        try {
            const now = Date.now();
            await addDoc(collection(db, 'users', auth.currentUser.uid, 'notes'), {
                title,
                text,
                pinned: false,
                isQuickNote: false,
                createdAt: now,
                updatedAt: now,
            });
            setDraftTitle('');
            setDraft('');
        } catch (err) {
            console.error('[Notes] add failed:', err);
            alert('Gagal simpan nota. Sila cuba lagi.');
        } finally {
            setIsSaving(false);
        }
    };

    // ─── Edit ───
    const startEdit = (note) => {
        setEditingId(note.id);
        setEditTitle(note.title || '');
        setEditText(note.text || '');
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditTitle('');
        setEditText('');
    };
    const handleSaveEdit = async (id) => {
        const title = editTitle.trim();
        const text = editText.trim();
        if (!auth.currentUser) return;
        if (!title && !text) { cancelEdit(); return; }
        try {
            await updateDoc(doc(db, 'users', auth.currentUser.uid, 'notes', id), {
                title,
                text,
                updatedAt: Date.now(),
            });
        } catch (err) {
            console.error('[Notes] update failed:', err);
            alert('Gagal kemaskini nota. Sila cuba lagi.');
        } finally {
            cancelEdit();
        }
    };

    // ─── Padam ───
    const handleDelete = async (id) => {
        if (!auth.currentUser) return;
        if (!confirm('Padam nota ini?')) return;
        try {
            await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'notes', id));
        } catch (err) {
            console.error('[Notes] delete failed:', err);
            alert('Gagal padam. Sila cuba lagi.');
        }
    };

    // ─── Toggle Pin ───
    const togglePin = async (note) => {
        if (!auth.currentUser) return;
        try {
            await updateDoc(doc(db, 'users', auth.currentUser.uid, 'notes', note.id), {
                pinned: !note.pinned,
                updatedAt: Date.now(),
            });
        } catch (err) {
            console.error('[Notes] toggle pin failed:', err);
            alert('Gagal kemaskini pin. Sila cuba lagi.');
        }
    };

    // ─── Toggle Quick Note ───
    const toggleQuick = async (note) => {
        if (!auth.currentUser) return;
        try {
            await updateDoc(doc(db, 'users', auth.currentUser.uid, 'notes', note.id), {
                isQuickNote: !note.isQuickNote,
                updatedAt: Date.now(),
            });
        } catch (err) {
            console.error('[Notes] toggle quick failed:', err);
            alert('Gagal kemaskini Quick Note. Sila cuba lagi.');
        }
    };

    // ─── Salin satu baris ke clipboard ───
    const copyLine = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
        } catch (err) {
            console.error('[Notes] copy failed:', err);
        }
    };

    const copyBtn = (value, key) => (
        <button
            onClick={(e) => { e.stopPropagation(); copyLine(value, key); }}
            title="Salin baris ini"
            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95 transition-all"
        >
            <span className="material-symbols-outlined text-[18px]">{copiedKey === key ? 'check' : 'content_copy'}</span>
        </button>
    );

    // ─── Filter ikut search (tajuk + isi, case-insensitive) ───
    const filteredNotes = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return notes;
        return notes.filter(n =>
            (n.title || '').toLowerCase().includes(q) ||
            (n.text || '').toLowerCase().includes(q)
        );
    }, [notes, searchQuery]);

    const isFiltering = searchQuery.trim() !== '';

    // ─── Loading ───
    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8">
            <div className="max-w-2xl mx-auto">
                {/* ─── Header ─── */}
                <div className="flex items-center gap-3 mb-6">
                    {onMenuClick && (
                        <button
                            onClick={onMenuClick}
                            aria-label="Buka menu"
                            className="md:hidden -ml-1.5 w-10 h-10 flex items-center justify-center rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 24 }}>menu</span>
                        </button>
                    )}
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/25 shrink-0">
                        <span className="material-symbols-outlined text-[22px] sm:text-[24px]" style={{ fontVariationSettings: "'FILL' 1" }}>sticky_note_2</span>
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">Notes</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {isFiltering ? `${filteredNotes.length} / ${notes.length} nota` : `${notes.length} nota`}
                        </p>
                    </div>
                </div>

                {/* ─── Add box (tajuk + isi) ─── */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 mb-4 shadow-sm">
                    <input
                        type="text"
                        value={draftTitle}
                        onChange={(e) => setDraftTitle(e.target.value)}
                        placeholder="Tajuk (pilihan)"
                        className="w-full bg-transparent text-[15px] font-semibold text-slate-800 dark:text-slate-100 placeholder:text-slate-400 placeholder:font-normal outline-none mb-1"
                    />
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAdd(); }}
                        placeholder="Tulis atau paste link…"
                        rows={3}
                        className="w-full bg-transparent text-[15px] text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none resize-none"
                    />
                    <div className="flex justify-end mt-2">
                        <button
                            onClick={handleAdd}
                            disabled={(!draftTitle.trim() && !draft.trim()) || isSaving}
                            className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSaving ? 'Menyimpan…' : 'Tambah'}
                        </button>
                    </div>
                </div>

                {/* ─── Search ─── */}
                {notes.length > 0 && (
                    <div className="relative mb-4">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-slate-400 pointer-events-none">search</span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Cari nota…"
                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl pl-10 pr-9 py-2.5 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-shadow"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                aria-label="Kosongkan carian"
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        )}
                    </div>
                )}

                {/* ─── List / Empty / No-match ─── */}
                {notes.length === 0 ? (
                    <div className="text-center py-16 px-6">
                        <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">sticky_note_2</span>
                        <p className="mt-3 text-slate-500 dark:text-slate-400 text-sm">
                            Belum ada nota. Tulis atau paste link pertama anda di atas.
                        </p>
                    </div>
                ) : filteredNotes.length === 0 ? (
                    <div className="text-center py-12 px-6">
                        <span className="material-symbols-outlined text-[40px] text-slate-300 dark:text-slate-700">search_off</span>
                        <p className="mt-3 text-slate-500 dark:text-slate-400 text-sm">
                            Tiada nota padan dengan "<span className="font-semibold">{searchQuery}</span>".
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredNotes.map((note) => (
                            <div
                                key={note.id}
                                className={`bg-white dark:bg-slate-900 border rounded-2xl p-4 shadow-sm transition-colors ${
                                    note.pinned
                                        ? 'border-amber-200 dark:border-amber-700/40'
                                        : 'border-slate-200 dark:border-slate-800'
                                }`}
                            >
                                {editingId === note.id ? (
                                    /* ── Edit mode ── */
                                    <div>
                                        <input
                                            type="text"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            placeholder="Tajuk (pilihan)"
                                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 text-[15px] font-semibold text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-200 mb-2"
                                        />
                                        <textarea
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            rows={3}
                                            autoFocus
                                            placeholder="Tulis atau paste link…"
                                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-[15px] text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
                                        />
                                        <div className="flex justify-end gap-2 mt-2">
                                            <button
                                                onClick={cancelEdit}
                                                className="px-4 py-1.5 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                            >
                                                Batal
                                            </button>
                                            <button
                                                onClick={() => handleSaveEdit(note.id)}
                                                className="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
                                            >
                                                Simpan
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    /* ── View mode (tap untuk edit, ikon copy setiap baris) ── */
                                    <>
                                        <div onClick={() => startEdit(note)} className="cursor-pointer space-y-0.5">
                                            {note.title && (
                                                <div className="flex items-start gap-2">
                                                    <h3 className="flex-1 min-w-0 text-[15px] font-bold text-slate-800 dark:text-white break-words">
                                                        {note.title}
                                                    </h3>
                                                    {copyBtn(note.title, `${note.id}:title`)}
                                                </div>
                                            )}
                                            {note.text && note.text.split('\n').map((line, i) => (
                                                line.trim() ? (
                                                    <div key={i} className="flex items-start gap-2">
                                                        <div className="flex-1 min-w-0 text-[15px] text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
                                                            {renderTextWithLinks(line)}
                                                        </div>
                                                        {copyBtn(line, `${note.id}:${i}`)}
                                                    </div>
                                                ) : (
                                                    <div key={i} className="h-2" />
                                                )
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                            <span className="text-xs text-slate-400 shrink-0">{formatRelative(note.createdAt)}</span>
                                            <div className="flex items-center gap-0.5">
                                                <button
                                                    onClick={() => togglePin(note)}
                                                    title={note.pinned ? 'Buang pin' : 'Sematkan ke atas'}
                                                    className={`p-1.5 rounded-lg transition-all active:scale-95 ${
                                                        note.pinned
                                                            ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                                                            : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                                >
                                                    <span
                                                        className="material-symbols-outlined text-[18px]"
                                                        style={{ fontVariationSettings: note.pinned ? "'FILL' 1" : undefined }}
                                                    >
                                                        push_pin
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={() => toggleQuick(note)}
                                                    title={note.isQuickNote ? 'Buang dari Quick Notes' : 'Tambah ke Quick Notes (Workspace)'}
                                                    className={`p-1.5 rounded-lg transition-all active:scale-95 ${
                                                        note.isQuickNote
                                                            ? 'text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10'
                                                            : 'text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                                >
                                                    <span
                                                        className="material-symbols-outlined text-[18px]"
                                                        style={{ fontVariationSettings: note.isQuickNote ? "'FILL' 1" : undefined }}
                                                    >
                                                        bolt
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(note.id)}
                                                    className="ml-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 active:scale-95 transition-all"
                                                >
                                                    Padam
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotesView;
